import { useState } from 'react'
import { Clock, Flame, Zap, Moon, Trash2, Edit, ChevronDown, ChevronRight, AlertCircle, PauseCircle, CalendarClock } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import type { Task } from '@/types'
import { useDeadlines } from '@/hooks/useDeadlines'
import { useIsPaused } from '@/hooks/useIsPaused'
import { cn, toLocalDate } from '@/lib/utils'
import { useToday } from '@/hooks/useToday'
import { dateKey, shiftDateKey } from '@trackmind/core'
import { TaskInfoTip } from '@/components/TaskInfoTip'
import { BlockerChips } from '@/components/BlockerChips'

interface TaskCardProps {
  task: Task
  onComplete: (id: string) => void
  onEdit: (task: Task) => void
  onDelete: (id: string) => void
  subtasks?: Task[]
  scheduledDate?: string | null  // When this task is scheduled
  /** One-tap −/+ for countable tasks ("read 5 papers" → 3/5) */
  onStepCount?: (id: string, delta: number) => void
  /** Given by a view that can filter by tag; makes the tag chips live. */
  onSelectTag?: (tag: string) => void
  /** Unticking a completed task takes the completion back. */
  onUncomplete?: (id: string) => void
}

const energyIcons = {
  high: <Flame className="h-4 w-4 text-orange-500" />,
  medium: <Zap className="h-4 w-4 text-yellow-500" />,
  low: <Moon className="h-4 w-4 text-blue-400" />,
}

const priorityColors: Record<number, string> = {
  1: 'bg-gray-500',
  2: 'bg-blue-500',
  3: 'bg-yellow-500',
  4: 'bg-orange-500',
  5: 'bg-red-500',
}

// Get color for tag based on its category
const getTagColor = (tag: string): string => {
  if (tag.startsWith('Projects/')) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  if (tag.startsWith('Work/')) return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
  if (tag.startsWith('Personal/')) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
  if (tag.startsWith('Finance')) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
  if (tag === 'urgent') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
}

// Get display name for tag (last part of hierarchical tag)
const getTagDisplayName = (tag: string): string => {
  const parts = tag.split('/')
  return parts[parts.length - 1]
}

export function TaskCard({ task, onComplete, onEdit, onDelete, subtasks = [], scheduledDate, onStepCount, onSelectTag, onUncomplete }: TaskCardProps) {
  const todayKey = useToday()
  const deadlines = useDeadlines()
  const isPaused = useIsPaused()
  const [expanded, setExpanded] = useState(false)
  const isCompleted = task.status === 'completed'

  // Countable progress ("read 5 papers"): partial credit instead of binary
  const targetCount = task.target_count ?? 0
  const isCountable = targetCount > 0
  const currentCount = Math.max(0, task.current_count ?? 0)
  const countPct = isCompleted ? 100 : isCountable ? Math.min(100, (currentCount / targetCount) * 100) : 0

  // Filter out project tags for cleaner display (project is usually shown elsewhere)
  const displayTags = task.tags?.filter(t => !t.startsWith('Projects/')) || []

  // Format scheduled date for display
  const formatScheduledDate = (dateStr: string): string => {
    // A plain date is that local day (toLocalDate), and "Today" is the user's
    // today. `new Date(dateStr)` read a plain date as UTC midnight — the day
    // before, in the Americas — so tomorrow's work said "Today".
    const date = toLocalDate(dateStr)
    const day = dateKey(date)
    const today = todayKey()
    if (day === today) return 'Today'
    if (day === shiftDateKey(today, 1)) return 'Tomorrow'

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <Card className={cn(
      "transition-all hover:shadow-md",
      isCompleted && "opacity-60",
      task.needs_review && "border-amber-500/50 bg-amber-50/30 dark:bg-amber-950/20"
    )}>
      <CardContent className="p-3 md:p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={isCompleted}
            // Unticking undoes it — the same control both ways, which is
            // what a checkbox implies. Falls back to doing nothing when the
            // host hasn't wired an undo.
            onCheckedChange={() => (isCompleted ? onUncomplete?.(task.id) : onComplete(task.id))}
            className="mt-1"
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 md:gap-2 mb-1 [&>span]:shrink-0 [&>div]:shrink-0">
              {subtasks.length > 0 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="p-0.5 hover:bg-accent rounded"
                >
                  {expanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              )}

              <h3 className={cn(
                "font-medium truncate",
                isCompleted && "line-through text-muted-foreground"
              )}>
                {task.title}
              </h3>

              {task.is_habit && (
                <Badge variant="secondary" className="hidden text-xs md:inline-flex">Habit</Badge>
              )}

              {isPaused(task) && (
                <Badge variant="outline" className="text-xs text-gray-500 border-gray-400">
                  <PauseCircle className="h-3 w-3 md:mr-1" />
                  <span className="hidden md:inline">Paused</span>
                </Badge>
              )}

              {task.needs_review && (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-500">
                  <AlertCircle className="h-3 w-3 md:mr-1" />
                  <span className="hidden md:inline">Review</span>
                </Badge>
              )}


              {/* Scheduled date - only show for non-completed tasks */}
              {scheduledDate && !isCompleted && !task.is_habit && (
                <Badge variant="outline" className="text-xs text-blue-600 border-blue-400">
                  <CalendarClock className="h-3 w-3 mr-1" />
                  {formatScheduledDate(scheduledDate)}
                </Badge>
              )}

              {/* Unscheduled indicator */}
              {!scheduledDate && !isCompleted && !task.is_habit && !isPaused(task) && (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                  <AlertCircle className="h-3 w-3 md:mr-1" />
                  <span className="hidden md:inline">Unscheduled</span>
                </Badge>
              )}
            </div>

            {/* Tags display — clickable when the surrounding view can filter
                by them. A tag that does nothing when you press it reads as
                decoration, which is what these were. */}
            {displayTags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {displayTags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    role={onSelectTag ? 'button' : undefined}
                    tabIndex={onSelectTag ? 0 : undefined}
                    onClick={onSelectTag ? (e) => { e.stopPropagation(); onSelectTag(tag) } : undefined}
                    onKeyDown={onSelectTag ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        onSelectTag(tag)
                      }
                    } : undefined}
                    className={cn(
                      "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium",
                      getTagColor(tag),
                      onSelectTag && "cursor-pointer hover:ring-1 hover:ring-primary/50"
                    )}
                    title={onSelectTag ? `Show everything tagged ${tag}` : tag}
                  >
                    {getTagDisplayName(tag)}
                  </span>
                ))}
                {displayTags.length > 3 && (
                  <span className="text-[10px] text-muted-foreground">
                    +{displayTags.length - 3} more
                  </span>
                )}
              </div>
            )}

            {task.description && (
              <p className="hidden text-sm text-muted-foreground mb-2 md:line-clamp-2">
                {task.description}
              </p>
            )}

            {/* What it waits for — stated, not alarmed about, and removable
                one chip at a time. See BlockerChips for why this stopped
                being red. */}
            {!isCompleted && <BlockerChips task={task} className="mb-2" />}

            {/* The meta strip is a pictogram — a clock, a flame, a coloured
                dot — so it's the thing that needs explaining. Hovering it (or
                tapping it, since a finger produces no hover) says what all of
                it means in words. */}
            <TaskInfoTip task={task} scheduledFor={scheduledDate}>
              <div className="flex w-fit cursor-help items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {task.estimated_minutes}m
                </span>

                <span className="flex items-center gap-1">
                  {energyIcons[task.energy_level]}
                  <span className="hidden md:inline">{task.energy_level}</span>
                </span>

                <span className={cn(
                  "w-2 h-2 rounded-full",
                  priorityColors[task.priority]
                )} />

                {task.deadline && (
                  <span className="text-orange-500">
                    Due: {deadlines.date(task)?.toLocaleDateString()}
                  </span>
                )}
              </div>
            </TaskInfoTip>

            {/* Countable progress row: −/+ steppers are always visible (they're
                the touch affordance) and update partial credit in place. */}
            {isCountable && !isCompleted && (
              <div className="mt-2 flex items-center gap-2">
                {onStepCount && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    aria-label="Decrease count"
                    disabled={currentCount <= 0}
                    onClick={() => onStepCount(task.id, -1)}
                  >
                    −
                  </Button>
                )}
                <span className="text-xs font-medium tabular-nums shrink-0">
                  {currentCount}/{targetCount}{task.count_unit ? ` ${task.count_unit}` : ''}
                </span>
                {onStepCount && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    aria-label="Increase count"
                    onClick={() => onStepCount(task.id, 1)}
                  >
                    +
                  </Button>
                )}
                <div className="flex-1 min-w-0 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${countPct}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground w-9 text-right shrink-0">
                  {Math.round(countPct)}%
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-0.5 md:flex-row md:items-center md:gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(task)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onDelete(task.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {expanded && subtasks.length > 0 && (
          <div className="mt-3 ml-8 pl-3 border-l-2 border-muted space-y-2">
            {subtasks.map((subtask) => (
              <div
                key={subtask.id}
                className="flex items-center gap-2 text-sm"
              >
                <Checkbox
                  checked={subtask.status === 'completed'}
                  onCheckedChange={() => subtask.status !== 'completed' && onComplete(subtask.id)}
                  className="h-3 w-3"
                />
                <span className={cn(
                  subtask.status === 'completed' && "line-through text-muted-foreground"
                )}>
                  {subtask.title}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
